/*
 * OemApplicationDetails.jsx
 * Copyright (c) Harman International Industries, Incorporated, 2024 - 2025
 * All rights reserved.
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { fetchPortfolioContentDetails } from "../../../Services/redux/Application-details/ApplicationDetailsActions";
import Topheader from "../../../Layout/header/header";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ListingHeader from "../../../Components/ListingHeader/ListingHeader";
import ApplicationDetailsAPKFile from "../../../Components/APKFile/ApplicationDetailsAPKFile";
import {
  Backdrop,
  CircularProgress,
  Container,
  Grid2,
  Box,
  Typography,
  IconButton,
  Modal,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ApplicationDetailsSideMenu from "../../../Components/ApplicationDetailsSideMenu/ApplicationDetailsSideMenu";
import ApplicationQaReportsPopUp from "../../../Components/ApplicationQaReportsPopUp/ApplicationQaReportsPopUp";
import AppDetailsLanguage from "../../../Components/ApplicationDetails-Language/AppDetailsLanguage";
import ApplicationDetailsIcon from "../../../Components/ApplicationDetailsIcon/ApplicationDetailsIcon";
import ApplicationDetailsClusterCompatibity from "../../../Components/ApplicationDetailsClusterCompatibity/ApplicationDetailsClusterCompatibity";
import ApplicationDetailsAdditionalDocument from "../../../Components/ApplicationDetailsAdditionalDocument/ApplicationDetailsAdditionalDocument";
import AppDetailsLicensing from "../../../Components/ApplicationDetails-Licensing/AppDetailsLicensing";
import ApplicationDetailsCategory from "../../../Components/ApplicationDetailsCategory/ApplicationDetailsCategory";
import ApplicationDetailsRollout from "../../../Components/ApplicationDetailsRollout/ApplicationDetailsRollout";
import OemAdminApplicationDetailsTargeting from "../../../Components/oem-admin-application-details-targeting/OemAdminApplicationDetailsTargeting";
import ApplicationDetailsLegalDocuments from "../../../Components/ApplicationDetailsLegalDocuments/ApplicationDetailsLegalDocuments";
import OemAdminMonetize from "../../../Components/OEM-Admin-Components/OemAdminMonetize/OemAdminMonetize";
import ApplicationDetailsFlexMetaData from "../../../Components/ApplicationDetailsFlexMetaData/ApplicationDetailsFlexMetaData";
import ApplicationDetailsDSADetails from "../../../Components/ApplicationDetailsDSADetails/ApplicationDetailsDSADetails";
import { checkEuropeanCountries, checkTagCountries } from "../../../Services/redux/DSAOemAdminUrl/DSAOemAdminActions";
import ApplicationDetailsAgeRatingAds from "../../../Components/ApplicationDetails-Age-Rating-Ads/ApplicationDetailsAgeRatingAds";
import OemAdminAppDependency from "../../../Components/OemAdminAppDependency/OemAdminAppDependency"
import PropTypes from "prop-types";
import { getConfig } from "../../../config";
import "./OemApplicationDetails.css";

// Loading Component
const LoadingBackdrop = ({ open }) => (
  <Backdrop
    sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
    open={open}
  >
    <CircularProgress color="inherit" />
  </Backdrop>
);

LoadingBackdrop.propTypes = {
  open: PropTypes.bool.isRequired,
};

function OemApplicationDetails() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { loading, submissionDetails } = useSelector(
    (state) => state.submissionDetails
  );
  const [lastFetchedAppId, setLastFetchedAppId] = useState(null); // Track last fetched appId
  const [showQAReports, setShowQAReports] = useState([]);
  const [open, setOpen] = useState(false);

  // Memoized values for better performance
  const isLoading = loading;
  const hasSubmissionDetails = useMemo(
    () => submissionDetails && Object.keys(submissionDetails).length > 0,
    [submissionDetails]
  );

  const isIdpLogin = useMemo(
    () => sessionStorage.getItem("idpLogin") === "true",
    []
  );

  const showMonetization = useMemo(
    () =>
      hasSubmissionDetails &&
      submissionDetails.isMonetized &&
      (submissionDetails.isInAppPurchase || submissionDetails.isPremiumApp),
    [hasSubmissionDetails, submissionDetails]
  );

  const showLegalDocuments = useMemo(
    () => hasSubmissionDetails && isIdpLogin && submissionDetails.isOemApp,
    [hasSubmissionDetails, isIdpLogin, submissionDetails]
  );

  const showLicensing = useMemo(
    () => hasSubmissionDetails && submissionDetails.isOemApp,
    [hasSubmissionDetails, submissionDetails]
  );


  const [isPatching, setIsPatching] = useState(false); // Track patch operations

  // Fetch data on mount or when appId changes
  useEffect(() => {
    // Only fetch if appId is different from last fetched or if no data exists
    // Don't refetch during patch operations
    if (
      appId &&
      !isPatching &&
      (appId !== lastFetchedAppId || (!hasSubmissionDetails && !loading))
    ) {
      sessionStorage.setItem("contentId", appId);

      dispatch(
        fetchPortfolioContentDetails(
          sessionStorage.getItem("portfolioId"),
          appId
        )
      );
      dispatch(checkTagCountries([]));

      setLastFetchedAppId(appId);
    }
  }, [
    appId,
    lastFetchedAppId,
    hasSubmissionDetails,
    loading,
    dispatch,
    isPatching,
  ]);

  // Check European countries for DSA details
  useEffect(() => {
    if (hasSubmissionDetails && Array.isArray(submissionDetails?.tags)) {
      const europeanCountries = getConfig()?.CHECK_EUROPEAN_COUNTRIES;
      if (!europeanCountries || !Array.isArray(europeanCountries)) return;
      const contentCountryTag = submissionDetails.tags.find(tag => tag.key === "country");
      if (!contentCountryTag?.values || !Array.isArray(contentCountryTag.values)) return;
      const countryValues = [...contentCountryTag.values];
      dispatch(checkEuropeanCountries(countryValues.some(code => europeanCountries.includes(code))));
    }
  }, [hasSubmissionDetails, submissionDetails, dispatch]);

  // Memoized callback
  const backToApplications = useCallback(() => {
    sessionStorage.removeItem("currentLanguage");
    navigate("/ui/oem-admin/applications");
  }, [navigate]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // useEffect to set showQAReports when submissionDetails changes
  useEffect(() => {
    if (submissionDetails?.qaReports?.length > 0) {
      const showTwoQAReports = submissionDetails.qaReports.slice(0, 2);
      setShowQAReports(showTwoQAReports);
    }
  }, [submissionDetails]);

  // Early return for loading state
  if (isLoading) {
    return (
      <>
        <LoadingBackdrop open={true} />
        <Topheader />
      </>
    );
  }

  // Early return if no data
  if (!hasSubmissionDetails) {
    return (
      <>
        <Topheader />
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mt: 4 }}>No data found.</Box>
        </Container>
      </>
    );
  }

  return (
    <>
      <Topheader />
      <Container maxWidth="lg">
        <Grid2 container>
          <Grid2 size={12}>
            <Box
              sx={{ display: "flex", flexDirection: "row" }}
              className="container"
            >
              {/* Side Menu */}
              <Box className="side-menu" sx={{ marginTop: "69px" }}>
                <Box className="arrow-back" onClick={backToApplications}>
                  <ArrowBackIcon sx={{ color: "#717273" }} />
                  <Box className="my-apps">My Apps</Box>
                </Box>
                <ApplicationDetailsSideMenu
                  submissionData={submissionDetails}
                />
              </Box>

              {/* Main Content */}
              <Box
                className="submission-container"
                sx={{ width: "100%", marginLeft: "13%" }}
              >
                {/* Fixed Header */}
                <Box
                  className="fixed-content-oem-admin-application-details"
                  sx={{ width: "1010px" }}
                >
                  <ListingHeader />
                </Box>

                {/* Scrollable Content */}
                <Box className="scrollable-content">
                  {/* QA Reports */}
                  {submissionDetails?.qaReports?.length > 0 && (
                      <Box
                        className="qaReports-section"
                        style={{ marginLeft: "10%" }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginRight: "16px",
                          }}
                        >
                          <div style={{ marginLeft: "16px" }}>
                            <Typography
                              sx={{
                                fontWeight: "600",
                                fontSize: "22px",
                              }}
                            >
                              Harman QA Test Results
                            </Typography>
                            <span
                              style={{ fontSize: "16px", fontWeight: "600" }}
                            >
                              Reports
                            </span>
                          </div>

                          {submissionDetails.qaReports.length > 2 && (
                            <Typography
                              sx={{
                                fontWeight: "500",
                                fontSize: "14px",
                                lineHeight: "100%",
                                letterSpacing: "0%",
                                color: "#2A2C2D",
                                cursor: "pointer",
                              }}
                              onClick={handleOpen}
                            >
                              View More Reports
                            </Typography>
                          )}
                        </Box>
                        <ApplicationQaReportsPopUp qaReports={showQAReports} />
                      </Box>
                    )}

                  {/* APK File */}
                  <ApplicationDetailsAPKFile
                    submissionData={submissionDetails}
                  />

                  {/* App Dependency */}
                  <OemAdminAppDependency />
                  {/* Language Details */}
                  <AppDetailsLanguage submissionData={submissionDetails} />

                  {/* Icons */}
                  <ApplicationDetailsIcon submissionData={submissionDetails} />
                  <ApplicationDetailsAgeRatingAds/>
                  {/* Cluster Compatibility - IDP only */}
                  {isIdpLogin && <ApplicationDetailsClusterCompatibity />}

                  <ApplicationDetailsFlexMetaData submissionData={submissionDetails}/>

                  {/* Additional Documents */}
                  <ApplicationDetailsAdditionalDocument
                    submissionData={submissionDetails}
                  />

                  {/* Licensing - OEM Apps only */}
                  {showLicensing && <AppDetailsLicensing />}

                  {/* Category */}
                  <ApplicationDetailsCategory
                    submissionData={submissionDetails}
                  />

                  {/* Legal Documents - IDP + OEM Apps only */}
                  {showLegalDocuments && <ApplicationDetailsLegalDocuments />}

                  {/* DSA Details - OEM Apps only */}
                  <ApplicationDetailsDSADetails />

                  {/* Targeting */}
                  <OemAdminApplicationDetailsTargeting
                    targettingData={submissionDetails}
                    appId={appId}
                    onPatchingStateChange={setIsPatching}
                  />

                  {/* Rollout */}
                  <ApplicationDetailsRollout />

                  {/* Monetization - if enabled */}
                  {showMonetization && <OemAdminMonetize />}
                </Box>
              </Box>
            </Box>
          </Grid2>
        </Grid2>
      </Container>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box className="qaReportPopUp" sx={{ width: "900px", height: "444px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "10px",
            }}
          >
            <Typography
              id="modal-modal-title"
              fontSize={"22px"}
              fontWeight={"600"}
              marginLeft={"18px"}
            >
              All Reports
            </Typography>
            <button style={{ cursor: "pointer", background: "none", border: "none", padding: 0 }} onClick={handleClose}>
              <IconButton component="span">
                <CloseIcon />
              </IconButton>
            </button>
          </div>
          <div
            className="qaReports-popup-section"
            style={{ height: "360px", overflowY: "auto" }}
          >
            {submissionDetails?.qaReports && (
              <ApplicationQaReportsPopUp
                qaReports={submissionDetails.qaReports}
              />
            )}
          </div>
        </Box>
      </Modal>
    </>
  );
}

export default OemApplicationDetails;
